import { createMutationAtom } from 'atoms/create-resource-atom';
import { atom, useAtom, useAtomValue } from 'jotai';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  Fragment,
} from 'react';
import { DcaForecast } from 'types/api-payloads';
import {
  DeclineCurveAnalysisTableResponse,
  type ProductionDataSource,
} from 'types/api-responses';
import { formatMutationResource } from 'utils/misc';
import {
  InlineLoading,
  Button,
  InlineNotification,
  DataTableSkeleton,
} from '@carbon/react';
import { EmptyState } from 'components/empty-state';
import DocumentSvg from 'images/srp-illustration-document.svg';
import style from './decline-curve-analysis-table.module.scss';
import { parseMomentUtc } from 'utils/dates';
import { Download } from '@carbon/react/icons';
import { debounce } from 'lodash-es';
import { format0 } from '../../../utils/number';
import { DcaFormValues, exportDcaToXlsx } from './export-dca-to-xlsx';
import {
  forecastSettingsFormAtom,
  gasModelFormAtom,
  oilModelFormAtom,
  waterModelFormAtom,
} from '../atoms';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
  ColumnPinningState,
  ColumnDef,
} from '@tanstack/react-table';
import { DndContext, DragMoveEvent, closestCenter } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useColumnFilter } from 'lib/hooks/useColumnFilter';
import { useColumnState } from 'lib/hooks/useColumnState';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import {
  DragHeader,
  getPinningStyles,
  getInitialColumnOrder,
  handleDragEnd,
  handleDragMove,
} from 'components/table/draggable';
import { ColumnFilter } from 'components/table/column-filter';
import { TableActions } from 'components/table/table-actions';
import { MIN_WIDTH } from 'components/table/column-actions';
import { HiddenColumns } from 'components/table/hidden-columns';
import { DebouncedInput } from 'components/debounced-input';
import { Pagination } from 'components/pagination';
import {
  itemRangeText,
  pageRangeText,
} from 'components/search/common/format-helpers';

interface DeclineCurveAnalysisTableProps {
  uwis: string;
  wellName?: string;
  tableId?: string;
  /**
   * The production version and the forecast the table is fetched for. Omitted
   * when `rows` is given, since there is then nothing to fetch.
   */
  versionId?: string;
  /** The source versionId is a version of. IHS when omitted. */
  productionSource?: ProductionDataSource;
  dcaForecast?: Array<DcaForecast>;
  dcaForecastData?: Array<DcaForecast>;
  isValidDcaForecast?: boolean;
  resetTable?: boolean;
  onResetSuccess?: () => void;
  /**
   * Rows to show instead of asking the server for them. The Well Analysis
   * section already holds the production and forecast volumes the table is
   * built from, so it merges them itself rather than having the forecast run
   * again.
   */
  rows?: Array<DcaRow>;
  /** True while the caller is still fetching the volumes `rows` is built from. */
  rowsLoading?: boolean;
  /**
   * The DCA parameters the export's parameters sheet reports. Callers that
   * pass `rows` pass these too, since the parameters are the saved forecast's
   * rather than whatever the configuration panel currently holds.
   */
  exportFormValues?: DcaFormValues;
}

const PAGE_SIZES = [16, 24, 48, 64, 100];

type DcaRow = {
  productionDate: string;
  monthsOnline: number;
  oilProductionVolume: number | null;
  gasProductionVolume: number | null;
  waterProductionVolume: number | null;
  oilModelForecast: number | null;
  gasModelForecast: number | null;
  waterModelForecast: number | null;
  grossOil: number | null;
  grossGas: number | null;
  grossWater: number | null;
};

const columnHelper = createColumnHelper<DcaRow>();

const columns: ColumnDef<DcaRow, any>[] = [
  columnHelper.accessor('productionDate', {
    id: 'productionDate',
    header: 'Date',
    size: 120,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('monthsOnline', {
    id: 'monthsOnline',
    header: 'Months Online',
    size: 120,
    cell: (info) => formatNumberWithCommas(info.getValue()),
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('oilProductionVolume', {
    id: 'oilProductionVolume',
    header: 'Oil Production Volumes (bbl)',
    size: 200,
    cell: (info) => formatNumberWithCommas(info.getValue()),
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('gasProductionVolume', {
    id: 'gasProductionVolume',
    header: 'Gas Production Volumes (mcf)',
    size: 200,
    cell: (info) => formatNumberWithCommas(info.getValue()),
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('waterProductionVolume', {
    id: 'waterProductionVolume',
    header: 'Water Production Volumes (bbl)',
    size: 200,
    cell: (info) => formatNumberWithCommas(info.getValue()),
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('oilModelForecast', {
    id: 'oilModelForecast',
    header: 'Oil Model Forecast (bbl)',
    size: 180,
    cell: (info) => formatNumberWithCommas(info.getValue()),
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('gasModelForecast', {
    id: 'gasModelForecast',
    header: 'Gas Model Forecast (mcf)',
    size: 180,
    cell: (info) => formatNumberWithCommas(info.getValue()),
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('waterModelForecast', {
    id: 'waterModelForecast',
    header: 'Water Model Forecast (bbl)',
    size: 180,
    cell: (info) => formatNumberWithCommas(info.getValue()),
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('grossOil', {
    id: 'grossOil',
    header: 'Gross Oil (bbl)',
    size: 150,
    cell: (info) => formatNumberWithCommas(info.getValue()),
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('grossGas', {
    id: 'grossGas',
    header: 'Gross Gas (mcf)',
    size: 150,
    cell: (info) => formatNumberWithCommas(info.getValue()),
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('grossWater', {
    id: 'grossWater',
    header: 'Gross Water (bbl)',
    size: 150,
    cell: (info) => formatNumberWithCommas(info.getValue()),
    meta: {
      filterVariant: 'range',
    },
  }),
];

const initialColumnPinning: ColumnPinningState = { left: [] };
const initialColumnOrder = getInitialColumnOrder(columns);

export const DeclineCurveAnalysisTable = ({
  uwis,
  versionId = '',
  productionSource = 'ihs',
  dcaForecast = [],
  dcaForecastData = [],
  isValidDcaForecast = false,
  wellName,
  tableId = 'dca-forecast-table',
  resetTable,
  onResetSuccess,
  rows: providedRows,
  rowsLoading = false,
  exportFormValues,
}: DeclineCurveAnalysisTableProps) => {
  // Rows handed in come with everything the table needs, so the two fetches
  // below stay off.
  const fetchesRows = providedRows === undefined;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);

  // Read content of form atoms for the dca parameters sheet
  const forecastSettingsForm = useAtomValue(forecastSettingsFormAtom);
  const oilModelForm = useAtomValue(oilModelFormAtom);
  const gasModelForm = useAtomValue(gasModelFormAtom);
  const waterModelForm = useAtomValue(waterModelFormAtom);

  const fallbackAtom = useMemo(
    () =>
      atom({
        value: null,
        isValid: true,
        isDirty: false,
        error: null,
      }),
    []
  );

  const oilStartDate = useAtomValue(oilModelForm.startDateAtomField);
  const gasStartDate = useAtomValue(gasModelForm.startDateAtomField);
  const waterStartDate = useAtomValue(waterModelForm.startDateAtomField);

  const oilQi = useAtomValue(oilModelForm.qiAtomField);
  const gasQi = useAtomValue(gasModelForm.qiAtomField);
  const waterQi = useAtomValue(waterModelForm.qiAtomField);

  const oilDi = useAtomValue(oilModelForm.diAtomField);
  const gasDi = useAtomValue(gasModelForm.diAtomField);
  const waterDi = useAtomValue(waterModelForm.diAtomField);

  const oilBFactor = useAtomValue(
    oilModelForm.bFactorAtomField ?? fallbackAtom
  );
  const gasBFactor = useAtomValue(
    gasModelForm.bFactorAtomField ?? fallbackAtom
  );
  const waterBFactor = useAtomValue(
    waterModelForm.bFactorAtomField ?? fallbackAtom
  );

  const oilDlim = useAtomValue(oilModelForm.dlimAtomField ?? fallbackAtom);
  const gasDlim = useAtomValue(gasModelForm.dlimAtomField ?? fallbackAtom);
  const waterDlim = useAtomValue(waterModelForm.dlimAtomField ?? fallbackAtom);

  const oilDeclineModel = useAtomValue(oilModelForm.declineModelAtomField);
  const gasDeclineModel = useAtomValue(gasModelForm.declineModelAtomField);
  const waterDeclineModel = useAtomValue(waterModelForm.declineModelAtomField);

  const oilRampupModel = useAtomValue(oilModelForm.rampupModelAtomField);
  const gasRampupModel = useAtomValue(gasModelForm.rampupModelAtomField);
  const waterRampupModel = useAtomValue(waterModelForm.rampupModelAtomField);

  const oilRampupDuration = useAtomValue(
    oilModelForm.rampupDurationAtomField ?? fallbackAtom
  );
  const gasRampupDuration = useAtomValue(
    gasModelForm.rampupDurationAtomField ?? fallbackAtom
  );
  const waterRampupDuration = useAtomValue(
    waterModelForm.rampupDurationAtomField ?? fallbackAtom
  );

  const oilQiRampup = useAtomValue(
    oilModelForm.qiRampupAtomField ?? fallbackAtom
  );
  const gasQiRampup = useAtomValue(
    gasModelForm.qiRampupAtomField ?? fallbackAtom
  );
  const waterQiRampup = useAtomValue(
    waterModelForm.qiRampupAtomField ?? fallbackAtom
  );

  const oilN = useAtomValue(oilModelForm.nAtomField ?? fallbackAtom);
  const gasN = useAtomValue(gasModelForm.nAtomField ?? fallbackAtom);
  const waterN = useAtomValue(waterModelForm.nAtomField ?? fallbackAtom);

  const oilRi = useAtomValue(oilModelForm.riAtomField ?? fallbackAtom);
  const gasRi = useAtomValue(gasModelForm.riAtomField ?? fallbackAtom);
  const waterRi = useAtomValue(waterModelForm.riAtomField ?? fallbackAtom);

  const oilTElf = useAtomValue(oilModelForm.tElfAtomField ?? fallbackAtom);
  const gasTElf = useAtomValue(gasModelForm.tElfAtomField ?? fallbackAtom);
  const waterTElf = useAtomValue(waterModelForm.tElfAtomField ?? fallbackAtom);

  const oilM = useAtomValue(oilModelForm.mAtomField ?? fallbackAtom);
  const gasM = useAtomValue(gasModelForm.mAtomField ?? fallbackAtom);
  const waterM = useAtomValue(waterModelForm.mAtomField ?? fallbackAtom);

  const diFormat = useAtomValue(forecastSettingsForm.diFormat);
  const qiFormat = useAtomValue(forecastSettingsForm.qiFormat);
  const forecastDuration = useAtomValue(forecastSettingsForm.forecastDuration);
  const forecastDurationOption = useAtomValue(
    forecastSettingsForm.forecastDurationOption
  );

  const declineCurveAnalysisTableAtom = useMemo(() => {
    return createMutationAtom<
      DeclineCurveAnalysisTableResponse,
      'declineCurveAnalysisTable'
    >();
  }, []);

  const [
    declineCurveAnalysisTableResponse,
    setDeclineCurveAnalysisTableResponse,
  ] = useAtom(declineCurveAnalysisTableAtom);

  const debouncedSetDeclineCurveAnalysisTableResponse = useMemo(() => {
    return debounce(setDeclineCurveAnalysisTableResponse, 300);
  }, [setDeclineCurveAnalysisTableResponse]);

  useEffect(() => {
    if (!fetchesRows) return;
    if ((versionId && uwis && dcaForecast.length === 0) || resetTable) {
      debouncedSetDeclineCurveAnalysisTableResponse({
        url: '/reservoir/dca_forecasts/dca_table',
        type: 'POST',
        data: {
          productionSource,
          productionVolumes: [{ uwis: [uwis], versionIds: [versionId] }],
          dcaForecast: [],
        },
        onSuccess: () => {
          onResetSuccess?.();
        },
      });
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId, productionSource, uwis, resetTable]);

  useEffect(() => {
    if (!fetchesRows) return;
    if (versionId && dcaForecastData?.length > 0 && isValidDcaForecast) {
      debouncedSetDeclineCurveAnalysisTableResponse({
        url: '/reservoir/dca_forecasts/dca_table',
        type: 'POST',
        data: {
          productionSource,
          productionVolumes: [{ uwis: [uwis], versionIds: [versionId] }],
          dcaForecast: dcaForecastData.map((dca) => ({
            ...dca,
            productionVersionIds: [versionId],
          })),
        },
      });
      setPage(1);
    }
  }, [
    fetchesRows,
    dcaForecastData,
    debouncedSetDeclineCurveAnalysisTableResponse,
    versionId,
    productionSource,
    uwis,
    isValidDcaForecast,
  ]);

  const { data, loading, error } = formatMutationResource(
    declineCurveAnalysisTableResponse
  );

  const declineCurveAnalysisTablePartialErrors = fetchesRows
    ? (data?.dcaTable[0].errors ?? [])
    : [];
  const rows: DcaRow[] = useMemo(() => {
    const rawRows = providedRows ?? data?.dcaTable[0]?.dcaRows ?? [];
    // Default sort is by production date
    return [...rawRows].sort((a, b) =>
      parseMomentUtc(a.productionDate).diff(parseMomentUtc(b.productionDate))
    );
  }, [data, providedRows]);

  const {
    columnFilters,
    setColumnFilters,
    isColumnFilterActive,
    setIsColumnFilterActive,
  } = useColumnFilter(tableId);

  const [columnSizing, setColumnSizing] = useColumnSizing(tableId);

  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId,
      initialColumnOrder,
      initialColumnPinning,
    });

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const resetCustomizations = useCallback(() => {
    setColumnFilters([]);
    setIsColumnFilterActive(false);
  }, [setColumnFilters, setIsColumnFilterActive]);

  const table = useReactTable({
    data: rows,
    columns,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: MIN_WIDTH,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
      columnFilters,
      sorting,
      columnOrder,
      columnPinning,
      columnSizing,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    initialState: {
      columnPinning: initialColumnPinning,
      columnOrder: initialColumnOrder,
    },
    meta: {
      reset: {
        resetCustomizations,
      },
    },
  });

  const filteredRows = table.getRowModel().rows;
  const totalItems = filteredRows.length;
  const hasData = rows.length > 0; // NOTE: *unfiltered* # of rows
  const isColumnVisible = table.getVisibleLeafColumns().length > 0;

  const paginatedRows = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredRows.slice(startIndex, endIndex);
  }, [filteredRows, page, pageSize]);

  const handlePaginate = ({
    page: newPage,
    pageSize: newPageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  // What the export writes: the rows on screen, wrapped in the response shape
  // the exporter reads.
  const exportTableData: DeclineCurveAnalysisTableResponse | null = fetchesRows
    ? (data ?? null)
    : {
        dcaTable: [
          {
            uwi: uwis,
            productionVolumeVersionId: versionId,
            dcaRows: rows,
            errors: [],
          },
        ],
      };

  const handleExportToXlsx = async () => {
    if (!exportTableData || isExporting) return;

    setIsExporting(true);
    setShowSuccessNotification(false);
    setExportError(null);
    try {
      const formValues = exportFormValues ?? {
        diFormat: diFormat.value,
        qiFormat: qiFormat.value,
        forecastDuration: forecastDuration.value,
        forecastDurationOption: forecastDurationOption.value,
        oil: {
          dlim: oilDlim.value,
          bFactor: oilBFactor.value,
          declineModel: oilDeclineModel.value,
          qi: oilQi.value,
          di: oilDi.value,
          startDate: oilStartDate.value,
          rampupModel: oilRampupModel.value,
          qiRampup: oilQiRampup.value,
          rampupDuration: oilRampupDuration.value,
          n: oilN.value,
          ri: oilRi.value,
          tElf: oilTElf.value,
          m: oilM.value,
        },
        gas: {
          dlim: gasDlim.value,
          bFactor: gasBFactor.value,
          declineModel: gasDeclineModel.value,
          qi: gasQi.value,
          di: gasDi.value,
          startDate: gasStartDate.value,
          rampupModel: gasRampupModel.value,
          qiRampup: gasQiRampup.value,
          rampupDuration: gasRampupDuration.value,
          n: gasN.value,
          ri: gasRi.value,
          tElf: gasTElf.value,
          m: gasM.value,
        },
        water: {
          dlim: waterDlim.value,
          bFactor: waterBFactor.value,
          declineModel: waterDeclineModel.value,
          qi: waterQi.value,
          di: waterDi.value,
          startDate: waterStartDate.value,
          rampupModel: waterRampupModel.value,
          qiRampup: waterQiRampup.value,
          rampupDuration: waterRampupDuration.value,
          n: waterN.value,
          ri: waterRi.value,
          tElf: waterTElf.value,
          m: waterM.value,
        },
      };

      exportDcaToXlsx({
        uwi: uwis,
        wellName,
        tableData: exportTableData,
        formValues,
      });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 5000);
    } catch (error) {
      console.error('Error exporting to XLSX:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while exporting data.';
      setExportError(errorMessage);
      setTimeout(() => setExportError(null), 10000);
    } finally {
      setIsExporting(false);
    }
  };

  if (fetchesRows ? loading : rowsLoading) {
    return (
      <div className={style.container}>
        <div className={style.divider} />
        <DataTableSkeleton
          data-testid="dca-table-skeleton"
          columnCount={11}
          rowCount={10}
          showHeader={false}
          showToolbar={false}
        />
      </div>
    );
  }

  return (
    <div className={style.container}>
      <div className={style.divider} />
      {error && declineCurveAnalysisTablePartialErrors.length > 0 && (
        <InlineNotification
          kind="error"
          title="Error"
          lowContrast
          className={style.errorMessage}
        >
          <div>
            <div>
              {declineCurveAnalysisTablePartialErrors.length > 0
                ? declineCurveAnalysisTablePartialErrors.map(
                    (partialError, index) => (
                      <div key={partialError.code || index}>
                        Stream: {partialError.details?.streamType || 'unknown'},
                        Field: {partialError.details?.field || 'unknown'},
                        Message: {partialError.message}
                      </div>
                    )
                  )
                : null}
            </div>
            <div>
              {error?.type
                ? error && 'error' in error.message
                  ? error.message.error
                  : 'An error occurred while loading the decline curve analysis table.'
                : null}
            </div>
          </div>
        </InlineNotification>
      )}
      {showSuccessNotification && (
        <InlineNotification
          kind="success"
          title="Export Successful"
          subtitle="Your DCA forecast data has been exported to Excel."
          lowContrast
          hideCloseButton={false}
          onCloseButtonClick={() => setShowSuccessNotification(false)}
          className={style.successMessage}
        />
      )}
      {exportError && (
        <InlineNotification
          kind="error"
          title="Export Failed"
          subtitle={exportError || 'Failed to export data to Excel.'}
          lowContrast
          hideCloseButton={false}
          onCloseButtonClick={() => setExportError(null)}
          className={style.exportErrorMessage}
        />
      )}
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={(e) =>
          handleDragEnd(
            e,
            columnPinning,
            columnOrder,
            setColumnOrder,
            setColumnPinning,
            hoveredColumnIdRef
          )
        }
        modifiers={[restrictToHorizontalAxis]}
        onDragMove={onDragMove}
      >
        <div className={style.toolbarTitle}>
          <p className={style.toolbarTitleText}>DCA Forecast Table</p>
        </div>
        <div ref={tableWrapperRef} className={style.stackTableArea}>
          <div className={style.tableContext}>
            <div className={style.stackSearchArea}>
              <DebouncedInput
                className={style.stackSearchField}
                value={globalFilter ?? ''}
                onChange={(value) => setGlobalFilter(String(value).trim())}
                placeholder="Search columns..."
              />
            </div>
            <TableActions
              table={table}
              showExport={false} // NOTE: using our manual Excel export below
              isColumnFilterActive={isColumnFilterActive}
              setIsColumnFilterActive={setIsColumnFilterActive}
            />
            <Button
              renderIcon={Download}
              onClick={handleExportToXlsx}
              kind="primary"
              iconDescription="Export Data to Excel"
              disabled={isExporting || totalItems === 0}
            >
              {isExporting ? (
                <InlineLoading description="Exporting..." status="active" />
              ) : (
                'Export to Excel'
              )}
            </Button>
          </div>

          {!hasData ? (
            <div className={style.emptyStateWrapper}>
              <EmptyState
                icon={DocumentSvg}
                headerText="No Decline Curve Analysis Table Available"
                helperText=""
              />
            </div>
          ) : !isColumnVisible ? (
            <HiddenColumns />
          ) : (
            <table className={style.stackTable}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Fragment key={headerGroup.id}>
                    <tr>
                      <SortableContext
                        items={columnOrder}
                        strategy={horizontalListSortingStrategy}
                      >
                        {headerGroup.headers.map((header) => (
                          <DragHeader
                            key={header.id}
                            header={header}
                            hoveredColumnIdRef={hoveredColumnIdRef}
                          />
                        ))}
                      </SortableContext>
                    </tr>
                    {isColumnFilterActive && (
                      <tr>
                        <ColumnFilter
                          headers={headerGroup.headers}
                          pinStyleGenerator={getPinningStyles}
                        />
                      </tr>
                    )}
                  </Fragment>
                ))}
              </thead>
              <tbody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{
                            ...getPinningStyles(cell.column),
                            width: cell.column.getSize(),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className={style.noResultsCell}
                    >
                      No results match your search or filter criteria
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {hasData && (
          <Pagination
            pageSizes={PAGE_SIZES}
            pageSize={pageSize}
            page={page}
            totalItems={totalItems}
            onChange={handlePaginate}
            itemRangeText={itemRangeText}
            pageRangeText={pageRangeText}
          />
        )}
      </DndContext>
    </div>
  );
};

function formatNumberWithCommas(num: number | string | null): string {
  if (num === null || num === undefined || num === '') return '-';
  const n = Number(num);
  if (isNaN(n)) return '-';
  return format0(n);
}
